import React, { useCallback, useEffect, useState } from 'react';
import { axiosInstance } from '../../App';
import { toast } from 'sonner';
import {
  Send, CheckCircle2, XCircle, Clock, AlertTriangle, Loader2,
  ShieldCheck, ChevronRight, Hash, User as UserIcon, Sparkles,
  MessageSquare, FileText, ExternalLink,
} from 'lucide-react';


const STATUS_AR = {
  DRAFT:             { ar: 'مُسَوَّدَة',         tone: 'bg-slate-100 text-slate-700' },
  PENDING_APPROVAL:  { ar: 'بِانتِظار الاعتِماد', tone: 'bg-amber-100 text-amber-800' },
  APPROVED:          { ar: 'مُعتَمَد',           tone: 'bg-emerald-100 text-emerald-700' },
  POSTED:            { ar: 'مُرَحَّل',            tone: 'bg-sky-100 text-sky-700' },
  LOCKED:            { ar: 'مَقفول',           tone: 'bg-zinc-200 text-zinc-700' },
  REJECTED:          { ar: 'مَرفوض',           tone: 'bg-rose-100 text-rose-700' },
  CANCELLED:         { ar: 'مُلغى',            tone: 'bg-slate-200 text-slate-600' },
};

const STEP_STATUS_AR = {
  pending:  { ar: 'بِانتِظار',  Icon: Clock,         cls: 'text-amber-600' },
  approved: { ar: 'مُعتَمَدَة',  Icon: CheckCircle2,  cls: 'text-emerald-600' },
  rejected: { ar: 'مَرفوضَة',   Icon: XCircle,       cls: 'text-rose-600' },
};

const ROLE_AR = {
  admin:           'إدارَة',
  finance_manager: 'مُدير مالي',
  accountant:      'مُحاسِب',
  site_engineer:   'مُهَندِس مَوقِع',
  project_manager: 'مُدير مَشروع',
  procurement:     'المُشتَريات',
  qc_engineer:     'مُهَندِس جودَة',
  consultant:      'الاستِشاري',
  hr_manager:      'مَوارِد بَشَرِيَّة',
  equipment_manager: 'مُدير مُعَدّات',
};

const EVENT_AR = {
  created:        'تَم الإنشاء',
  step_approved:  'خُطوَة مُعتَمَدَة',
  approved:       'تَم الاعتِماد كامِلاً',
  rejected:       'تَم الرَّفض',
  escalated:      'تَم التَّصعيد',
  posted:         'تَم التَّرحيل',
  cancelled:      'تَم الإلغاء',
  post_failed:    'فَشَل التَّرحيل',
};


export default function DocApprovalPanel({ doc, currentUser: currentUserProp, onMutated }) {
  const [data, setData]       = useState({ workflow: null, steps: [], events: [] });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [decidingId, setDecidingId] = useState(null);
  const [comment, setComment] = useState('');
  const [rejectMode, setRejectMode] = useState(false);
  const [linkedBill, setLinkedBill] = useState(null);

  // Resolve current user — prop wins; fallback to localStorage stamp.
  const currentUser = currentUserProp || (() => {
    try { return JSON.parse(localStorage.getItem('user') || 'null'); }
    catch { return null; }
  })();

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await axiosInstance.get(`/document-hub/${doc.id}/workflow`);
      setData(d);
      // Wave F3-B: also pull the linked bill (if any) for the summary card.
      try {
        const { data: lb } = await axiosInstance.get(
          `/document-hub/${doc.id}/linked-bill`);
        setLinkedBill(lb.bill || null);
      } catch { setLinkedBill(null); }
    } catch (e) {
      // 404 acceptable when nothing submitted yet
    } finally { setLoading(false); }
  }, [doc?.id]);

  useEffect(() => { refresh(); }, [refresh]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data: r } = await axiosInstance.post(
        `/document-hub/${doc.id}/submit-for-approval`,
        { note: comment || null },
      );
      toast.success(r.already_submitted
        ? 'كان مُرسَلاً بِالفِعل'
        : `تَم الإرسال — ${r.workflow.total_steps || 0} مَرحَلَة اعتِماد`);
      setComment('');
      await refresh();
      onMutated?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail
        : detail?.message || 'فَشَل الإرسال');
    } finally { setSubmitting(false); }
  };

  const decide = async (decision) => {
    if (!data.workflow) return;
    if (decision === 'reject' && !comment.trim()) {
      toast.error('يَجِب إدخال سَبَب الرَّفض');
      return;
    }
    setDecidingId(decision);
    try {
      const url = `/workflows/${data.workflow.id}/${decision === 'approve' ? 'approve' : 'reject'}`;
      const body = decision === 'approve'
        ? { comment: comment || null }
        : { reason: comment };
      await axiosInstance.post(url, body);
      toast.success(decision === 'approve' ? 'تَمَّ الاعتِماد' : 'تَمَّ الرَّفض');
      setComment(''); setRejectMode(false);
      await refresh();
      onMutated?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail
        : detail?.message || 'فَشَلَت العَمَلِيَّة');
    } finally { setDecidingId(null); }
  };

  const wf = data.workflow;
  const currentStepNo = wf?.current_step || 1;
  const myRole = currentUser?.role || '';
  const currentStep = (data.steps || []).find(
    (s) => s.step === currentStepNo && s.status === 'pending',
  );
  // Admin wildcard parity with backend: admin may approve any step on
  // any workflow, even their own submissions. Non-admins are blocked
  // from self-approval.
  const isAdmin = myRole === 'admin';
  const roleMatches = isAdmin || currentStep?.role === myRole;
  const notSelfOrAdmin = isAdmin || wf?.created_by !== currentUser?.id;
  const canDecide = wf?.status === 'PENDING_APPROVAL' && currentStep
    && roleMatches && notSelfOrAdmin;

  // Render
  if (loading) {
    return (
      <div className="text-center text-slate-400 py-6">
        <Loader2 className="w-5 h-5 animate-spin mx-auto" />
      </div>
    );
  }

  if (!wf) {
    return (
      <div className="space-y-3" data-testid="approval-panel">
        <div className="flex items-center gap-1 pb-2 border-b text-[10px] uppercase font-semibold text-slate-500">
          <ShieldCheck className="w-3 h-3" /> الاعتِماد (F3-A)
        </div>
        <div className="rounded-lg bg-violet-50 border border-violet-200 p-3 text-[11px] text-violet-900 leading-relaxed"
             data-testid="approval-empty">
          <div className="font-semibold mb-1">لَم يُرسَل لِلاعتِماد بَعد</div>
          <div className="text-violet-700">
            عِندَ الإرسال، يَنتَقِل المُستَنَد عَبر سِلسِلَة الاعتِماد بِناءً على
            <span className="font-mono"> النَّوع + المَبلَغ</span> (الـ Approval Matrix).
          </div>
        </div>
        <label className="block text-[11px]">
          <span className="text-slate-600">مُلاحَظَة (اختِيارِيَّة):</span>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            data-testid="approval-submit-note"
            placeholder="مَثَلاً: مُراجَعَة عاجِلَة لِلصَّرف الشَّهري"
            className="w-full mt-1 rounded border border-slate-300 px-2 py-1.5 text-xs h-16 resize-none"
          />
        </label>
        <button
          onClick={submit} disabled={submitting}
          data-testid="approval-submit-btn"
          className="w-full text-xs font-semibold rounded-lg bg-violet-600 hover:bg-violet-700
                     text-white px-3 py-2 inline-flex items-center justify-center gap-1
                     disabled:opacity-50"
        >
          {submitting
            ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
            : <Send className="w-3.5 h-3.5" />}
          إرسال لِلاعتِماد
        </button>
      </div>
    );
  }

  const tone = STATUS_AR[wf.status] || STATUS_AR.DRAFT;
  return (
    <div className="space-y-3" data-testid="approval-panel">
      {/* Header */}
      <div className="flex items-center justify-between gap-2 pb-2 border-b">
        <div className="flex items-center gap-1 text-[10px] uppercase font-semibold text-slate-500">
          <ShieldCheck className="w-3 h-3" /> الاعتِماد
        </div>
        <span
          className={`text-[10px] font-semibold rounded px-2 py-0.5 ${tone.tone}`}
          data-testid="approval-status-badge"
        >
          {tone.ar}
        </span>
      </div>

      {/* Summary card */}
      <div className="rounded-lg border bg-white p-2.5 space-y-1.5 text-[11px]" data-testid="approval-summary">
        <div className="flex items-center justify-between">
          <span className="text-slate-500">رَقم الـ workflow</span>
          <span className="font-mono text-slate-700">{wf.id.slice(0, 8)}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-slate-500">المَبلَغ</span>
          <span className="font-mono text-emerald-700">
            {Number(wf.amount || 0).toLocaleString('en')} ر.س
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-slate-500">القاعِدَة المُطَبَّقَة</span>
          <span className="font-mono text-violet-700 text-[10px]">{wf.rule_key || '—'}</span>
        </div>
        {wf.rule_description && (
          <div className="text-[10px] text-slate-500 bg-slate-50 rounded p-1.5 leading-relaxed">
            <Sparkles className="w-3 h-3 inline mr-1 text-violet-500" />
            {wf.rule_description}
          </div>
        )}
        <div className="flex items-center justify-between">
          <span className="text-slate-500">التَّقَدُّم</span>
          <span className="font-mono">
            {wf.current_step || 0} / {wf.total_steps || 0}
          </span>
        </div>
      </div>

      {/* Steps timeline */}
      {data.steps?.length > 0 && (
        <div className="rounded-lg border bg-white p-2" data-testid="approval-steps">
          <div className="text-[10px] font-semibold text-slate-500 mb-1.5">
            مَراحِل الاعتِماد
          </div>
          <ol className="space-y-1.5">
            {data.steps.map((s) => {
              const meta = STEP_STATUS_AR[s.status] || STEP_STATUS_AR.pending;
              const Icon = meta.Icon;
              const isCurrent = s.step === currentStepNo
                && s.status === 'pending' && wf.status === 'PENDING_APPROVAL';
              return (
                <li
                  key={s.id}
                  className={`flex items-start gap-2 p-1.5 rounded
                              ${isCurrent ? 'bg-amber-50 ring-1 ring-amber-200' : ''}`}
                  data-testid={`approval-step-${s.step}`}
                >
                  <Icon className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${meta.cls}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-1">
                      <span className="text-[11px] font-semibold">
                        {s.step}. {ROLE_AR[s.role] || s.role}
                      </span>
                      <span className={`text-[9px] ${meta.cls}`}>{meta.ar}</span>
                    </div>
                    {s.approver_user_name && (
                      <div className="text-[10px] text-slate-500 inline-flex items-center gap-1">
                        <UserIcon className="w-2.5 h-2.5" />
                        {s.approver_user_name}
                        {s.timestamp && (
                          <span className="font-mono ml-1">
                            · {s.timestamp.slice(0, 16).replace('T', ' ')}
                          </span>
                        )}
                      </div>
                    )}
                    {s.comment && (
                      <div className="text-[10px] text-slate-600 italic mt-0.5">
                        “{s.comment}”
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      )}

      {/* Decide controls */}
      {canDecide && (
        <div className="rounded-lg border-2 border-amber-300 bg-amber-50/40 p-2.5 space-y-2"
             data-testid="approval-decide-block">
          <div className="text-[11px] font-semibold text-amber-900 inline-flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" />
            دَورُك في الاعتِماد — المَرحَلَة {currentStepNo}
          </div>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            data-testid="approval-decide-comment"
            placeholder={rejectMode ? 'سَبَب الرَّفض (إلزامي)' : 'تَعليق (اختِيارِي)'}
            className="w-full rounded border border-slate-300 px-2 py-1.5 text-xs h-14 resize-none"
          />
          {!rejectMode ? (
            <div className="flex gap-1">
              <button
                onClick={() => decide('approve')}
                disabled={decidingId === 'approve'}
                data-testid="approval-approve-btn"
                className="flex-1 text-xs font-semibold rounded-lg bg-emerald-600 hover:bg-emerald-700
                           text-white px-2 py-1.5 inline-flex items-center justify-center gap-1
                           disabled:opacity-50"
              >
                {decidingId === 'approve'
                  ? <Loader2 className="w-3 h-3 animate-spin" />
                  : <CheckCircle2 className="w-3 h-3" />}
                اعتِماد
              </button>
              <button
                onClick={() => setRejectMode(true)}
                data-testid="approval-reject-mode"
                className="flex-1 text-xs font-semibold rounded-lg border-2 border-rose-300
                           text-rose-700 hover:bg-rose-50 px-2 py-1.5 inline-flex
                           items-center justify-center gap-1"
              >
                <XCircle className="w-3 h-3" /> رَفض
              </button>
            </div>
          ) : (
            <div className="flex gap-1">
              <button
                onClick={() => decide('reject')}
                disabled={decidingId === 'reject' || !comment.trim()}
                data-testid="approval-reject-confirm"
                className="flex-1 text-xs font-semibold rounded-lg bg-rose-600 hover:bg-rose-700
                           text-white px-2 py-1.5 inline-flex items-center justify-center gap-1
                           disabled:opacity-50"
              >
                {decidingId === 'reject'
                  ? <Loader2 className="w-3 h-3 animate-spin" />
                  : <XCircle className="w-3 h-3" />}
                تَأكيد الرَّفض
              </button>
              <button
                onClick={() => { setRejectMode(false); setComment(''); }}
                data-testid="approval-reject-cancel"
                className="text-xs font-semibold rounded-lg border border-slate-300
                           text-slate-700 hover:bg-slate-50 px-3 py-1.5"
              >
                إلغاء
              </button>
            </div>
          )}
        </div>
      )}

      {wf.status === 'PENDING_APPROVAL' && !canDecide && (
        <div className="text-[11px] text-slate-500 bg-slate-50 rounded p-2 text-center"
             data-testid="approval-waiting">
          بِانتِظار اعتِماد:{' '}
          <span className="font-semibold">
            {ROLE_AR[currentStep?.role] || currentStep?.role || '—'}
          </span>
        </div>
      )}

      {wf.status === 'REJECTED' && wf.rejection_reason && (
        <div className="text-[11px] text-rose-700 bg-rose-50 rounded p-2 leading-relaxed"
             data-testid="approval-rejection-reason">
          <span className="font-semibold inline-flex items-center gap-1">
            <XCircle className="w-3 h-3" /> سَبَب الرَّفض:
          </span>
          <div className="mt-0.5 italic">“{wf.rejection_reason}”</div>
        </div>
      )}

      {/* Linked Supplier Bill (Wave F3-B Phase 1) */}
      {linkedBill && (
        <div className="rounded-lg border-2 border-emerald-200 bg-emerald-50/40 p-2.5 space-y-1.5"
             data-testid="approval-linked-bill">
          <div className="flex items-center justify-between gap-2">
            <span className="text-[11px] font-semibold text-emerald-800 inline-flex items-center gap-1">
              <FileText className="w-3 h-3" /> فاتورَة مُورِد مُنشَأَة
            </span>
            <span className="text-[10px] font-mono text-emerald-900 bg-emerald-100 rounded px-1.5 py-0.5">
              {linkedBill.bill_number}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-x-2 gap-y-0.5 text-[10.5px] text-slate-700">
            <div><span className="text-slate-500">المُورِد:</span> {linkedBill.supplier_name || '—'}</div>
            <div><span className="text-slate-500">الحالَة:</span>{' '}
              <span className="font-mono">{linkedBill.status}</span></div>
            <div><span className="text-slate-500">المَبلَغ:</span>{' '}
              <span className="font-mono text-emerald-700">
                {Number(linkedBill.total || 0).toLocaleString('en')} ر.س
              </span></div>
            <div><span className="text-slate-500">ضَريبَة:</span>{' '}
              <span className="font-mono">
                {Number(linkedBill.vat_amount || 0).toLocaleString('en')}
              </span></div>
            <div><span className="text-slate-500">تاريخ:</span>{' '}
              <span className="font-mono">{linkedBill.bill_date}</span></div>
            <div><span className="text-slate-500">استِحقاق:</span>{' '}
              <span className="font-mono">{linkedBill.due_date}</span></div>
          </div>
          {linkedBill.flagged && (
            <div className="text-[10px] bg-amber-100 text-amber-900 rounded px-1.5 py-1 inline-flex items-center gap-1"
                 data-testid="approval-linked-bill-flags">
              <AlertTriangle className="w-3 h-3" />
              <span>تَحتاج مُراجَعَة: {(linkedBill.flag_reasons || []).join(', ')}</span>
            </div>
          )}
          <a
            href={`/bill-review/${linkedBill.id}`}
            target="_blank" rel="noreferrer"
            data-testid="approval-open-bill-link"
            className="text-[10.5px] font-semibold text-emerald-700 hover:text-emerald-900
                       inline-flex items-center gap-1"
          >
            <ExternalLink className="w-3 h-3" /> فَتح لَوحَة المُراجَعَة
          </a>
        </div>
      )}

      {/* Events log */}
      {data.events?.length > 0 && (
        <details className="text-[10px]" data-testid="approval-events">
          <summary className="cursor-pointer text-slate-500 hover:text-slate-700 font-semibold">
            سِجِل الأَحداث ({data.events.length})
          </summary>
          <ul className="mt-1.5 space-y-1 max-h-40 overflow-y-auto pr-1">
            {data.events.map((e, i) => (
              <li key={i} className="flex items-start gap-1 text-slate-600">
                <ChevronRight className="w-3 h-3 mt-0.5 shrink-0 text-slate-400" />
                <div className="flex-1 min-w-0">
                  <span className="font-semibold">{EVENT_AR[e.event] || e.event}</span>
                  {e.user_name && <span className="text-slate-500"> · {e.user_name}</span>}
                  <span className="font-mono text-slate-400 ml-1">
                    {e.timestamp?.slice(0, 16).replace('T', ' ')}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
